import { useMemo } from 'react'

import { Agreement01Icon } from '@hugeicons/react'

import { usePartnersTopLikes } from '@/features/partners/hooks/usePartnersTopLikes'
import {
  selectFeaturedPartnerId
} from '@/features/partners/lib/selectHomeCheckInPartners'
import { useEventSlug } from '@/shared/hooks/useEventSlug'
import { useNavigation } from '@/shared/hooks/useNavigation'
import { useRecords } from '@/shared/hooks/useRecords'
import { Card } from '@/shared/ui/Card'
import { SectionHeader } from '@/shared/ui/Layout'

import { HomePartnersMiniGrid } from '../../components/HomePartnersMiniGrid'
import { BlockEmptyState } from '../BlockEmptyState'
import { BlockSkeleton } from '../BlockSkeleton'

import type { BlockComponentProps } from '../index'

const DEFAULT_LIMIT = 6

interface PartnerRecord {
  id: number
  name?: string | null
  position?: number | null
  logo?: { file?: { url?: string | null } | null } | null
  reactions?: { like?: number }
}

/**
 * Parceiros do evento na home: grade compacta 3×2 com destaque visual para o
 * mais curtido. Clique leva à listagem — o card completo fica no módulo.
 */
export const PartnersBlock = ({ block }: BlockComponentProps) => {
  const eventSlug = useEventSlug()
  const { navigateTo } = useNavigation()

  const categoryIds = block.settings?.category_ids ?? []
  const limit = block.settings?.limit ?? DEFAULT_LIMIT
  const apiBasePath = eventSlug ? `/user/digital_products/${eventSlug}/partners` : ''

  const { records, isLoading } = useRecords({
    r: apiBasePath,
    params: categoryIds.length
      ? { filter: { partner_category_id_in: categoryIds } }
      : {},
    shouldFetch: !!eventSlug
  })

  const topLikeIds = usePartnersTopLikes(apiBasePath, !!eventSlug)

  const partners = (records ?? []) as PartnerRecord[]

  const featuredId = useMemo(
    () => selectFeaturedPartnerId(partners, topLikeIds),
    [partners, topLikeIds]
  )

  if (isLoading) return <BlockSkeleton rows={2} />

  if (partners.length === 0)
    return (
      <BlockEmptyState
        icon={Agreement01Icon}
        title="Nenhum parceiro por aqui"
        hint={
          categoryIds.length
            ? 'Nenhum parceiro nas categorias escolhidas para este bloco.'
            : 'Os parceiros do evento aparecem aqui assim que forem publicados.'
        }
      />
    )

  return (
    <Card className="card-gamification">
      <div className="px-4 pb-0.5 pt-3">
        <SectionHeader
          icon={<Agreement01Icon size={16} variant="stroke" aria-hidden />}
          title={block.title || 'Parceiros'}
        >
          <SectionHeader.Action onClick={() => navigateTo('/parceiros')}>
            Ver todos
          </SectionHeader.Action>
        </SectionHeader>
      </div>

      <Card.Body className="px-4 pb-3 pt-1.5">
        <HomePartnersMiniGrid
          partners={partners}
          topLikeIds={topLikeIds}
          featuredId={featuredId}
          limit={limit}
        />
      </Card.Body>
    </Card>
  )
}
